import { useEffect, useRef, useState } from "react";

interface DebouncedInputProps
    extends Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        "onChange" | "value"
    > {
    value: string | number;
    onChange: (value: string | number) => void;
    debounceMs?: number;
}

/**
 * Input component with built-in debouncing.
 * The onChange callback is called 300ms after the user stops typing.
 */
export function DebouncedInput({
    value: initialValue,
    onChange,
    debounceMs = 300,
    ...props
}: DebouncedInputProps) {
    const [value, setValue] = useState(initialValue);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        // Clear any pending timeout when component unmounts
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);

        // Clear existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Set new timeout
        timeoutRef.current = setTimeout(() => {
            // Convert to number if input type is number
            if (props.type === "number") {
                const numValue = Number(newValue);
                if (!isNaN(numValue) && newValue !== "") {
                    onChange(numValue);
                } else if (newValue === "") {
                    // Allow empty string for number inputs (user is clearing)
                    onChange(0);
                }
            } else {
                onChange(newValue);
            }
        }, debounceMs);
    };

    return <input {...props} value={value} onChange={handleChange} />;
}
